import Avatar from '../../avatar';
import Button from '../../button';
import {
  BulletIcon,
  ButtonSection,
  Email,
  EmailSection,
  Name,
  NameSection,
  Profile,
  ProfileSection,
  RedirectSection,
  Time,
  TimeAndStatusSection,
  UserInfo,
  UserProfileModal
} from './styles';
import { useCallback, useMemo } from 'react';
import copy from 'clipboard-copy';
import { captureException } from '@sentry/react';
import MessageGroupCacheService from '../../../services/messageGroupCacheService';
import { ChatGroupInterface } from '../../../interfaces/chatMessageInterface';
import { useHistory } from 'react-router-dom';
import { useMobileDevice } from '../../../global/useMobile';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { createMessageGroup } from '../../../services/chatMessageService';
import { setLastReadForDiscussion } from '../../../services/chatMessageSyncService';
import { dispatchAppSuccess } from '../../../services/appService';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { AVATAR_BORDER, BUTTON_TYPE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, USER_ROLE } from '../../../global/constants';
import IconButton from '../../button/iconButton';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ROUTES } from '../../../routes/urls';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
  onClose: () => void;
}

function UserModal({ onClose }: Props) {
  //mobx variables
  const { appStore, workspaceStore } = rootStore;
  const { userModalDetail } = appStore;
  const { currentWorkspace: workspace } = workspaceStore;

  // other variables
  const history = useHistory();
  const mobile = useMobileDevice();
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();

  // for copy email
  const onCopyLink = useCallback(async (url: string) => {
    try {
      await copy(url);
      dispatchAppSuccess('Email copied successfully!');
    } catch (error) {
      captureException(error);
    }
  }, []);
  const createNewGroup = useCallback(async () => {
    if (!workspace?.id || !userDetails?.id || !userModalDetail?.id) return;
    const payload = {
      userId: userDetails?.id,
      name: '',
      companyId: workspace?.id,
      users: [userModalDetail?.id]
    };
    const result = await createMessageGroup(payload);
    if (result) {
      rootStore.chatMessageStore.setCurrentChatGroup(null);
      UserPreferenceSingleton.getInstance().setLastMessageGroup(result?.id);
      const path = mobile ? `/messages/details?chatGroup=${result?.id}` : `/messages?chatGroup=${result?.id}`;
      history.push(path);
    }
  }, [history, mobile, userDetails?.id, userModalDetail?.id, workspace?.id]);

  const handleMessageClick = useCallback(async () => {
    try {
      const messageGroups = await MessageGroupCacheService.getInstance().getMessageGroups();
      const personalGroups = messageGroups?.filter((item: ChatGroupInterface) => item?.users?.length === 2);
      const currentUserGroup = personalGroups?.find((group: ChatGroupInterface) =>
        group?.users?.some((user) => user.id === userModalDetail?.id)
      );
      if (currentUserGroup) {
        if (mobile) history.push(`${ROUTES.NAVBARMESSAGE_DETAILS_MOBILE}?chatGroup=${currentUserGroup?.id}`);
        else history.push(`${ROUTES.NAVBARMESSAGE}?chatGroup=${currentUserGroup?.id}`);
        UserPreferenceSingleton.getInstance().setLastMessageGroup(currentUserGroup?.id);
        setLastReadForDiscussion();
        rootStore.chatMessageStore.setCurrentChatGroup(currentUserGroup);
      } else {
        await createNewGroup();
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      onClose();
    }
  }, [createNewGroup, history, mobile, onClose, userModalDetail?.id]);

  // for display assigned task page
  const handleAssignedTask = useCallback(() => {
    history.push(ROUTES.MEMBER_TASKS.replace(':id', userModalDetail?.id || ''));
    onClose();
  }, [history, onClose, userModalDetail?.id]);

  // get current time based on timezone
  const getCurrentTime = useMemo(() => {
    try {
      // Extracting IANA Timezone
      const match = userModalDetail?.timeZone?.match(/-\s*([\w/-]+)$/);
      const ianaZone = match ? match[1] : 'Asia/Kolkata';

      let formattedTime;
      try {
        // Check if the timezone is valid by trying to create a date in that timezone
        dayjs().tz(ianaZone);
        formattedTime = dayjs()
          .tz(ianaZone)
          .format(userDetails.dateFormat + ' , hh:mm A');
      } catch (error) {
        // Fallback to Asia/Kolkata if the timezone is invalid
        formattedTime = dayjs()
          .tz('Asia/Kolkata')
          .format(userDetails.dateFormat + ' , hh:mm A');
      }

      // Split into date and time
      const [date, time] = formattedTime.split(' , ');

      return { date, time };
    } catch (error) {
      console.error(`Invalid timezone format (${userModalDetail?.timeZone}):`, error);
      return { date: 'Invalid', time: 'Timezone' };
    }
  }, [userDetails.dateFormat, userModalDetail?.timeZone]);

  const shouldShowMessageButton = useMemo(() => {
    const currentUser = workspace?.users?.find((user) => user?.id === userDetails?.id);
    if (!currentUser) return true;
    const viewerId = currentUser?.id;
    const targetId = userModalDetail?.id;
    const viewerRole = currentUser?.role;
    const targetRole = userModalDetail?.role;

    // Hide if viewing own profile
    if (viewerId === targetId) return false;

    const isViewerClient = viewerRole === USER_ROLE.CLIENT;
    const isTargetClient = targetRole === USER_ROLE.CLIENT;

    const isViewerAdminOrOwner = viewerRole === USER_ROLE.ADMIN;
    const isTargetAdminOrOwner = targetRole === USER_ROLE.ADMIN;

    if (isViewerClient) return isTargetAdminOrOwner;
    if (!isViewerAdminOrOwner && isTargetClient) return false;
    if (isViewerAdminOrOwner) return true;
    return true;
  }, [userDetails?.id, userModalDetail?.id, userModalDetail?.role, workspace?.users]);

  return (
    <UserProfileModal>
      <ProfileSection>
        <UserInfo>
          <Profile>
            <Avatar
              imgSrc={userModalDetail?.profile_photo || ''}
              name={userModalDetail?.name || ''}
              size={42}
              borderVariant={AVATAR_BORDER.NONE}
            />
          </Profile>
          <NameSection>
            <Name>{userModalDetail?.name}</Name>
            <EmailSection>
              <Email>{userModalDetail?.email}</Email>
              <IconButton
                iconName='copy-icon'
                size={ICON_BUTTON_SIZE.EXTRA_SMALL}
                variant={ICON_BUTTON_TYPE.TERTIARY}
                onClick={() => onCopyLink(userModalDetail?.email || '')}
                className='copyIcon'
              />
            </EmailSection>
          </NameSection>
        </UserInfo>
        <IconButton
          iconName='new-close-icon'
          size={ICON_BUTTON_SIZE.SMALL}
          variant={ICON_BUTTON_TYPE.TERTIARY}
          onClick={onClose}
          className='closeIcon'
        />
      </ProfileSection>
      <RedirectSection>
        {shouldShowMessageButton && (
          <ButtonSection>
            <Button
              title='Message'
              iconName='message-icon'
              variant={BUTTON_TYPE.SECONDARY}
              className='button'
              onClick={handleMessageClick}
              iconPosition='start'
            />
          </ButtonSection>
        )}
        <Button
          onClick={handleAssignedTask}
          title='Assigned tasks'
          iconName='navbar-MyTask-icon'
          variant={BUTTON_TYPE.SECONDARY}
          className='button'
          iconPosition='start'
        />
      </RedirectSection>
      <TimeAndStatusSection>
        <Time>{getCurrentTime.date}</Time>
        <BulletIcon />
        <Time>{getCurrentTime.time}</Time>
      </TimeAndStatusSection>
    </UserProfileModal>
  );
}

export default observer(UserModal);
